import { useCallback, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  usePostFreshDiscardMutation,
  usePostFreshFreezeMutation,
  usePostFreshImplantMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';

import type {
  FreshStrawDiscardSharedInputs,
  FreshStrawFreezeSharedInputs,
  FreshStrawImplantSharedInputs,
  FreshStrawRow,
} from './payload';
import {
  BatchResult,
  runDiscardBatch,
  runFreezeBatch,
  runImplantBatch,
} from './saveBatch';

export interface UseFreshStrawActionsArgs {
  rows: FreshStrawRow[];
  implantShared: FreshStrawImplantSharedInputs;
  freezeShared: FreshStrawFreezeSharedInputs;
  discardShared: FreshStrawDiscardSharedInputs;
  /** Called after a successful batch (all rows saved). Use for refetches. */
  onSaved?: (
    action: 'implant' | 'freeze' | 'discard',
    rowsSaved: number,
  ) => void;
}

export interface UseFreshStrawActionsResult {
  handleImplant: () => Promise<void>;
  handleFreeze: () => Promise<void>;
  handleDiscard: () => Promise<void>;
  isSaving: boolean;
}

const LABEL: Record<'implant' | 'freeze' | 'discard', string> = {
  implant: 'Implant',
  freeze: 'Freeze',
  discard: 'Discard',
};

export function useFreshStrawActions(
  args: UseFreshStrawActionsArgs,
): UseFreshStrawActionsResult {
  const { rows, implantShared, freezeShared, discardShared, onSaved } = args;
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const [implantMutation] = usePostFreshImplantMutation();
  const [freezeMutation] = usePostFreshFreezeMutation();
  const [discardMutation] = usePostFreshDiscardMutation();

  const toastErrors = useCallback(
    (action: 'implant' | 'freeze' | 'discard', errors: string[]) => {
      dispatch(
        addToast({
          severity: 'error',
          summary: `${LABEL[action]} failed`,
          detail:
            errors.length > 3
              ? `${errors.slice(0, 3).join('; ')} (+${errors.length - 3} more)`
              : errors.join('; '),
          life: ToastLife.LONG,
        }),
      );
    },
    [dispatch],
  );

  const toastSuccess = useCallback(
    (action: 'implant' | 'freeze' | 'discard', rowsSaved: number) => {
      dispatch(
        addToast({
          severity: 'success',
          summary: `${LABEL[action]} complete`,
          detail: `Saved ${rowsSaved} row${rowsSaved === 1 ? '' : 's'}.`,
          life: ToastLife.SHORT,
        }),
      );
    },
    [dispatch],
  );

  const handleResult = useCallback(
    <T>(action: 'implant' | 'freeze' | 'discard', result: BatchResult<T>) => {
      if (result.ok) {
        toastSuccess(action, result.rowsSaved);
        onSaved?.(action, result.rowsSaved);
      } else {
        toastErrors(action, result.errors);
        // Partial success — still inform the caller so it can refetch.
        if (result.rowsSaved > 0) onSaved?.(action, result.rowsSaved);
      }
    },
    [onSaved, toastErrors, toastSuccess],
  );

  const handleImplant = useCallback(async () => {
    setIsSaving(true);
    try {
      const result = await runImplantBatch(rows, implantShared, (body) =>
        implantMutation(body).unwrap(),
      );
      handleResult('implant', result);
    } finally {
      setIsSaving(false);
    }
  }, [rows, implantShared, implantMutation, handleResult]);

  const handleFreeze = useCallback(async () => {
    setIsSaving(true);
    try {
      const result = await runFreezeBatch(rows, freezeShared, (body) =>
        freezeMutation(body).unwrap(),
      );
      handleResult('freeze', result);
    } finally {
      setIsSaving(false);
    }
  }, [rows, freezeShared, freezeMutation, handleResult]);

  const handleDiscard = useCallback(async () => {
    setIsSaving(true);
    try {
      const result = await runDiscardBatch(rows, discardShared, (body) =>
        discardMutation(body).unwrap(),
      );
      handleResult('discard', result);
    } finally {
      setIsSaving(false);
    }
  }, [rows, discardShared, discardMutation, handleResult]);

  return {
    handleImplant,
    handleFreeze,
    handleDiscard,
    isSaving,
  };
}
